import { Injectable } from '@angular/core';
import { Approval, EquipmentLog, EquipmentLogMapped } from '@core/interfaces';
import { StatusApprovalsPipe } from '@fuse/pipes/approvals-status/status-translate.pipe';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { chain, clone, isEqual, sortBy } from 'lodash';
import * as moment from 'moment-timezone';
import english from '../../../../i18n/en.json';
import spanish from '../../../../i18n/es.json';
import portuguese from '../../../../i18n/pt.json';

@Injectable({
  providedIn: 'root'
})
export class CreateEquipmentApprovalsLogsService {
  constructor(
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private translateService: TranslateService,
    private statusApprovalsPipe: StatusApprovalsPipe
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
  }

  public listApprovalsLogsMapped(equipmentLogs: EquipmentLog[], approvals: Approval[]): EquipmentLogMapped[] {
    const equipmentsLogsSorted = sortBy(equipmentLogs, (equipmentLog) => moment(equipmentLog.createdAt));
    const approvalsLogsSortedByCreatedAt = sortBy(approvals, (approvalLog) => moment(approvalLog.createdAt));

    const equipmentApprovalsLogsMapped = [];

    for (const approval of approvalsLogsSortedByCreatedAt) {
      const previousLog = equipmentsLogsSorted
        .filter((equipmentLog) => moment(equipmentLog.createdAt).isBefore(moment(approval.createdAt)))
        .pop();

      const approvalLog = {
        approval: approval,
        updatedBy: approval.resolvedBy,
        approvedStatus: 'pending',
        clientApplicationName: approval.clientApplicationName,
        icon: 'ic_scheduled_20px',
        eventName: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.APPROVALS.CREATED_TITLE', {
          status: this.statusApprovalsPipe.transform('pending').toLowerCase()
        }),
        colorClass: 'pending',
        formattedCreatedAt: moment(approval.createdAt).local().format('DD/MM/YYYY'),
        createdAt: moment(approval.createdAt),
        showDiffApproval: false,
        eventDescription: this.translateService.instant(
          'EQUIPMENTS.DIALOG.HISTORIC.EVENTS.APPROVALS.CREATED_DESCRIPTION',
          {
            number: approval.equipment.number,
            name: approval.equipment.name
          }
        )
      };

      if (!previousLog) {
        equipmentApprovalsLogsMapped.push({
          ...approvalLog,
          showMessage: false
        });
      }

      const approvedCreateEquipmentLog = this.generateApprovedApproval({ approvalLog, previousLog });

      if (approvedCreateEquipmentLog) {
        equipmentApprovalsLogsMapped.push(approvedCreateEquipmentLog);
      }

      const refusedCreateEquipmentLog = this.generateRefusedApproval({ approvalLog, previousLog });

      if (refusedCreateEquipmentLog) {
        equipmentApprovalsLogsMapped.push(refusedCreateEquipmentLog);
      }

      const updatedEquipmentLog = this.generateUpdateApproval({ approvalLog, previousLog });

      if (updatedEquipmentLog) {
        equipmentApprovalsLogsMapped.push(updatedEquipmentLog);
      }
    }

    return equipmentApprovalsLogsMapped;
  }

  private generateApprovedApproval({ approvalLog, previousLog }): EquipmentLogMapped {
    if (approvalLog.approval.status !== 'approved') {
      return null;
    }

    const approvedApprovalLog = clone(approvalLog);

    approvedApprovalLog.icon = 'ic_done_20px';
    approvedApprovalLog.colorClass = 'done';
    approvedApprovalLog.approvedStatus = approvalLog.approval.status;
    approvedApprovalLog.eventName = !previousLog
      ? this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.APPROVALS.CREATED_TITLE', {
          status: this.statusApprovalsPipe.transform(approvalLog.approval.status).toLowerCase()
        })
      : this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.APPROVALS.APPROVAL_TITLE', {
          status: this.statusApprovalsPipe.transform(approvalLog.approval.status).toLowerCase()
        });
    approvedApprovalLog.eventDescription = approvalLog.approval.notes
      ? this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NOTES.DESCRIPTION', {
          notes: approvalLog.approval.notes
        })
      : this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NOTES.WITHOUT_NOTES');
    approvedApprovalLog.createdAt = moment(approvalLog.approval.resolvedAt);
    approvedApprovalLog.formattedCreatedAt = moment(approvalLog.approval.resolvedAt).local().format('DD/MM/YYYY');
    approvedApprovalLog.updatedBy = approvalLog.approval.resolvedBy;

    return approvedApprovalLog;
  }

  private generateRefusedApproval({ approvalLog, previousLog }): EquipmentLogMapped {
    if (approvalLog.approval.status !== 'refused') {
      return null;
    }

    const refusedApprovalLog = clone(approvalLog);

    refusedApprovalLog.icon = 'ic_reported_20px';
    refusedApprovalLog.colorClass = 'refused';
    refusedApprovalLog.approvedStatus = approvalLog.approval.status;
    refusedApprovalLog.eventName = !previousLog
      ? this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.APPROVALS.CREATED_TITLE', {
          status: this.statusApprovalsPipe.transform(approvalLog.approval.status).toLowerCase()
        })
      : this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.APPROVALS.APPROVAL_TITLE', {
          status: this.statusApprovalsPipe.transform(approvalLog.approval.status).toLowerCase()
        });
    refusedApprovalLog.eventDescription = approvalLog.approval.notes
      ? this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NOTES.DESCRIPTION', {
          notes: approvalLog.approval.notes
        })
      : this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NOTES.WITHOUT_NOTES');
    refusedApprovalLog.createdAt = moment(approvalLog.approval.resolvedAt);
    refusedApprovalLog.formattedCreatedAt = moment(approvalLog.approval.resolvedAt).local().format('DD/MM/YYYY');
    refusedApprovalLog.updatedBy = approvalLog.approval.resolvedBy;

    return refusedApprovalLog;
  }

  private generateUpdateApproval({ approvalLog, previousLog }): EquipmentLogMapped {
    if (!previousLog) {
      return null;
    }

    const updatedApprovalLog = clone(approvalLog);

    updatedApprovalLog.attachments = this.approvalAttachments({ approvalLog, previousLog });
    updatedApprovalLog.customFields = this.approvalCustomFields({ approvalLog, previousLog });
    updatedApprovalLog.approval.notesDescription = this.approvalNotes({ approvalLog, previousLog });
    updatedApprovalLog.approval.nameDescription = this.approvalName({ approvalLog, previousLog });
    updatedApprovalLog.approval.typeDescription = this.approvalEquipmentType({ approvalLog, previousLog });
    updatedApprovalLog.approval.numberDescription = this.approvalNumber({ approvalLog, previousLog });
    updatedApprovalLog.approval.locationDescription = this.approvalLocation({ approvalLog, previousLog });
    updatedApprovalLog.eventDescription = null;
    updatedApprovalLog.showDiffApproval = true;
    updatedApprovalLog.eventName = this.translateService.instant(
      'EQUIPMENTS.DIALOG.HISTORIC.EVENTS.APPROVALS.APPROVAL_TITLE',
      {
        status: this.statusApprovalsPipe.transform('pending').toLowerCase()
      }
    );

    return updatedApprovalLog;
  }

  private approvalAttachments({ approvalLog, previousLog }): any {
    if (!(approvalLog.approval.equipment.attachments && approvalLog.approval.equipment.attachments.length)) {
      return null;
    }

    const approvalAttachments = sortBy(approvalLog.approval.equipment.attachments, (attachment) =>
      moment(attachment.createdAt)
    );
    const equipmentAttachments = sortBy(previousLog.snapshot.attachments, (attachment) => moment(attachment.createdAt));

    if (isEqual(approvalAttachments, equipmentAttachments)) {
      return null;
    }

    const attachmentsAdded = [];
    const attachmentsRemoved = [];

    approvalAttachments.forEach(({ id, path }) => {
      const existingAttachment = equipmentAttachments.find((equipmentAttachment) => {
        return equipmentAttachment.id === id;
      });

      if (!existingAttachment) {
        attachmentsAdded.push({
          id,
          path
        });
      }
    });

    equipmentAttachments.forEach(({ id, path }) => {
      const existingAttachment = approvalAttachments.find((approvalAttachment) => {
        return approvalAttachment.id === id;
      });

      if (!existingAttachment) {
        attachmentsRemoved.push({
          id,
          path
        });
      }
    });

    const attachments = {
      added: {
        title: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.ATTACHMENTS.ADDED'),
        items: attachmentsAdded
      },
      removed: {
        title: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.ATTACHMENTS.REMOVED'),
        items: attachmentsRemoved
      }
    };

    return attachments;
  }

  private approvalCustomFields({ approvalLog, previousLog }): any {
    if (!(approvalLog.approval && approvalLog.approval.equipment && approvalLog.approval.equipment.customFields && approvalLog.approval.equipment.customFields.length)) {
      return null;
    }

    const approvalCustomFields = chain(approvalLog.approval.equipment.customFields)
    .map(({ name, value, type }) => ({ name, value, type }))
    .sortBy('name')
    .value();

    const equipmentCustomFields = chain(previousLog.snapshot.customFields)
    .map(({ name, value, type }) => ({ name, value, type }))
    .sortBy('name')
    .value();

    if (isEqual(approvalCustomFields, equipmentCustomFields)) {
      return null;
    }

    const customsFieldsAdded = [];
    const customsFieldsUpdated = [];
    const customsFieldsRemoved = [];

    approvalCustomFields.forEach(({ name, value }) => {
      const existingCustomField = equipmentCustomFields.find((previousCustomField) => {
        return previousCustomField.name === name;
      });

      if (existingCustomField) {
        if (existingCustomField.value !== value) {
          customsFieldsUpdated.push({
            name: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CUSTOM_FIELDS.QUESTION', {
              question: name
            }),
            value: this.normalizeCustomFieldValue(value)
          });
        }
      } else {
        customsFieldsAdded.push({
          name: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CUSTOM_FIELDS.QUESTION', {
            question: name
          }),
          value: this.normalizeCustomFieldValue(value)
        });
      }
    });

    equipmentCustomFields.forEach(({ name, value }) => {
      const existingCustomField = approvalCustomFields.find((currentCustomField) => {
        return currentCustomField.name === name;
      });

      if (!existingCustomField) {
        customsFieldsRemoved.push({
          name: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CUSTOM_FIELDS.QUESTION', {
            question: name
          }),
          value: this.normalizeCustomFieldValue(value)
        });
      }
    });

    const customFields = {
      added: {
        title: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CUSTOM_FIELDS.ANSWER_ADDED'),
        items: customsFieldsAdded
      },
      updated: {
        title: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CUSTOM_FIELDS.ANSWER_UPDATED'),
        items: customsFieldsUpdated
      },
      removed: {
        title: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.CUSTOM_FIELDS.ANSWER_REMOVED'),
        items: customsFieldsRemoved
      }
    };
    return customFields;
  }

  private approvalNotes({ approvalLog, previousLog }): any {
    if (approvalLog.approval.equipment.notes === previousLog.snapshot.notes) {
      return null;
    }

    if (approvalLog.approval.equipment.notes && !previousLog.snapshot.notes) {
      return {
        title: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NOTES.CREATED'),
        notes: approvalLog.approval.equipment.notes
      };
    }

    if (!approvalLog.approval.equipment.notes && previousLog.snapshot.notes) {
      return {
        title: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NOTES.REMOVED'),
        notes: previousLog.snapshot.notes
      };
    }

    if (approvalLog.approval.equipment.notes !== previousLog.snapshot.notes) {
      return {
        title: this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NOTES.UPDATED'),
        notes: approvalLog.approval.equipment.notes
      };
    }
  }

  private approvalName({ approvalLog, previousLog }): any {
    if (approvalLog.approval.equipment.name === previousLog.snapshot.name) {
      return null;
    }

    return this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NAME.DESCRIPTION', {
      currentName: approvalLog.approval.equipment.name,
      previousName: previousLog.snapshot.name
    });
  }

  private approvalNumber({ approvalLog, previousLog }): any {
    if (approvalLog.approval.equipment.number === previousLog.snapshot.number) {
      return null;
    }

    return this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.NUMBER.DESCRIPTION', {
      currentNumber: approvalLog.approval.equipment.number,
      previousNumber: previousLog.snapshot.number
    });
  }

  private approvalEquipmentType({ approvalLog, previousLog }): any {
    const approvalTypeId = approvalLog.approval.equipment.type && approvalLog.approval.equipment.type.id;
    const equipmentTypeId = previousLog.snapshot.type && previousLog.snapshot.type.id;

    if (approvalTypeId === equipmentTypeId) {
      return null;
    }

    return this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.TYPE.DESCRIPTION', {
      previousType: previousLog.snapshot.type.name,
      currentType: approvalLog.approval.equipment.type.name
    });
  }

  private approvalLocation({ approvalLog, previousLog }): any {
    const approvalLocationId = approvalLog.approval.equipment.location && approvalLog.approval.equipment.location.id;
    const equipmentLocationId = previousLog.snapshot.location && previousLog.snapshot.location.id;

    if (approvalLocationId === equipmentLocationId) {
      return null;
    }

    if (approvalLog.approval.equipment.location === previousLog.snapshot.location) {
      return null;
    }

    return this.translateService.instant('EQUIPMENTS.DIALOG.HISTORIC.EVENTS.LOCATION.DESCRIPTION', {
      currentLocation: approvalLog.approval.equipment.location.name,
      previousLocation: previousLog.snapshot.location.name
    });
  }

  private normalizeCustomFieldValue(value): string {
    const datePattern = /^\d{4}-\d{2}-\d{2}/;
    return datePattern.test(value) ? moment(value).local().format('DD/MM/YYYY') : value;
  }

}
